import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';

interface AddClientPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  display_name: string;
  custom_email: string;
  custom_phone: string;
  private_notes: string;
  tags: string[];
  is_favorite: boolean;
}

interface FormErrors {
  display_name?: string;
  custom_email?: string;
  custom_phone?: string;
  contact?: string;
}

const AddClientPanel: React.FC<AddClientPanelProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<FormData>({
    display_name: '',
    custom_email: '',
    custom_phone: '',
    private_notes: '',
    tags: [],
    is_favorite: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const displayNameRef = useRef<HTMLInputElement>(null);

  // Focus on display name when panel opens
  useEffect(() => {
    if (isOpen && displayNameRef.current) {
      setTimeout(() => displayNameRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Email is optional if phone is provided
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional if email is provided
    const phoneRegex = /^\+?[\d\s\-()]+$/;
    return phoneRegex.test(phone);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Display name is required
    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required';
    }

    // At least email or phone must be provided
    if (!formData.custom_email.trim() && !formData.custom_phone.trim()) {
      newErrors.contact = 'Provide at least email or phone number';
    }

    // Validate email format if provided
    if (formData.custom_email && !validateEmail(formData.custom_email)) {
      newErrors.custom_email = 'Invalid email format';
    }

    // Validate phone format if provided
    if (formData.custom_phone && !validatePhone(formData.custom_phone)) {
      newErrors.custom_phone = 'Invalid phone format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await expertDashboardApi.expertClients.create({
        display_name: formData.display_name,
        custom_email: formData.custom_email || undefined,
        custom_phone: formData.custom_phone || undefined,
        private_notes: formData.private_notes || undefined,
        tags: formData.tags.length > 0 ? formData.tags : undefined,
        is_favorite: formData.is_favorite,
      });

      const { expert_client, message } = response.data;
      toast.success(message || 'Client added successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.errors?.join(', ') ||
                          error.response?.data?.error ||
                          'Failed to add client. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      display_name: '',
      custom_email: '',
      custom_phone: '',
      private_notes: '',
      tags: [],
      is_favorite: false,
    });
    setErrors({});
    setTagInput('');
    onClose();
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 20) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Add New Client</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close panel"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* Display Name */}
            <div>
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-1">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                ref={displayNameRef}
                type="text"
                id="display_name"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  errors.display_name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="John Smith"
              />
              {errors.display_name && (
                <p className="mt-1 text-sm text-red-600">{errors.display_name}</p>
              )}
            </div>

            {/* Contact Information Group */}
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <p className="text-sm text-gray-600 mb-3">
                Provide at least email or phone number
              </p>

              {/* Email */}
              <div className="mb-4">
                <label htmlFor="custom_email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="custom_email"
                  value={formData.custom_email}
                  onChange={(e) => setFormData({ ...formData, custom_email: e.target.value })}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                    errors.custom_email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                />
                {errors.custom_email && (
                  <p className="mt-1 text-sm text-red-600">{errors.custom_email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="custom_phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  id="custom_phone"
                  value={formData.custom_phone}
                  onChange={(e) => setFormData({ ...formData, custom_phone: e.target.value })}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                    errors.custom_phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="+1234567890"
                />
                {errors.custom_phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.custom_phone}</p>
                )}
              </div>

              {errors.contact && (
                <p className="mt-2 text-sm text-red-600">{errors.contact}</p>
              )}
            </div>

            {/* Private Notes */}
            <div>
              <label htmlFor="private_notes" className="block text-sm font-medium text-gray-700 mb-1">
                Private Notes <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <textarea
                id="private_notes"
                value={formData.private_notes}
                onChange={(e) => setFormData({ ...formData, private_notes: e.target.value })}
                rows={4}
                maxLength={500}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Add any notes about this client..."
              />
              <p className="mt-1 text-xs text-gray-500 text-right">
                {formData.private_notes.length}/500
              </p>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Tags <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  id="tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Add a tag..."
                  disabled={formData.tags.length >= 20}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim() || formData.tags.length >= 20}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-purple-500 hover:text-purple-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {formData.tags.length}/20 tags
              </p>
            </div>

            {/* Mark as Favorite */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_favorite}
                  onChange={(e) => setFormData({ ...formData, is_favorite: e.target.checked })}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Mark as favorite
                </span>
                <svg
                  className={`w-5 h-5 transition-colors ${
                    formData.is_favorite ? 'text-yellow-500' : 'text-gray-300'
                  }`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Adding...
              </span>
            ) : (
              'Add Client'
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddClientPanel;
